<template>
    <tab-layout
        :buttons="buttons"
        :permissions="permissions"
        @openAddress="openAddress(null)"
        @openAddressMap="openFullMap"
    >
        <template #title> {{ $t.__('overview.tabs.TabAddresses.title') }} </template>

        <template #content>
            <preloader :display="loading" class="mt-3" />

            <div v-if="!loading && addresses.length > 0" class="tab-addresses__list-addresses">
                <div v-if="mainInvoicingAddress">
                    <h4 class="typographies-heading-h4">
                        {{ titleMainAddress }}
                    </h4>
                    <AddressCard
                        :permissions="permissions"
                        :address="mainInvoicingAddress"
                        :addressUpdating="addressUpdating"
                        :disableSwitches="addressSelected"
                        @onEdit="openAddress"
                        @onDelete="deleteAddress(mainInvoicingAddress)"
                        @onDeliveryAddressSwitch="defineDeliveryAddress(mainInvoicingAddress)"
                        @onInvoicingAddressSwitch="defineInvoicingAddress(mainInvoicingAddress)"
                    />
                </div>
                <div
                    v-if="mainDeliveryAddress && mainDeliveryAddress.id !== mainInvoicingAddress.id"
                >
                    <h4 class="typographies-heading-h4">
                        {{ $t.__('overview.tabs.TabAddresses.title_delivery_address') }}
                    </h4>
                    <AddressCard
                        :permissions="permissions"
                        :address="mainDeliveryAddress"
                        :addressUpdating="addressUpdating"
                        :disableSwitches="addressSelected"
                        @onEdit="openAddress"
                        @onDelete="deleteAddress(mainDeliveryAddress)"
                        @onDeliveryAddressSwitch="defineDeliveryAddress(mainDeliveryAddress)"
                        @onInvoicingAddressSwitch="defineInvoicingAddress(mainDeliveryAddress)"
                    >
                    </AddressCard>
                </div>
                <div v-if="otherAddresses.length > 0">
                    <h4 class="typographies-heading-h4">
                        {{ $t.__('overview.tabs.TabAddresses.title_other_addresses') }}
                    </h4>
                    <div class="address-card-container">
                        <AddressCard
                            v-for="address in otherAddresses"
                            :key="address.id"
                            :permissions="permissions"
                            :address="address"
                            :addressUpdating="addressUpdating"
                            :disableSwitches="addressSelected"
                            @onEdit="openAddress"
                            @onDelete="deleteAddress(address)"
                            @onDeliveryAddressSwitch="defineDeliveryAddress(address)"
                            @onInvoicingAddressSwitch="defineInvoicingAddress(address)"
                        />
                    </div>
                </div>

                <AddressMap
                    :addresses="addresses"
                    :corpAddress="mainCorpAddress"
                    :visible="fullMapVisible"
                    @close="closeFullMap"
                />
            </div>

            <div v-else>
                <div v-if="!loading" class="placeholder">
                    <p>{{ $l10n.__('Aucune adresse définie') }}</p>
                </div>
            </div>
            <CompassModal
                v-model:visible="showDeleteModal"
                size="small"
                :title="$t.__('common.labels.delete_address_title')"
                :primaryAction="primaryActionDeleteAddress"
                :closeOnClickOverlay="false"
                :secondaryActions="[
                    {
                        label: $t.__('common.actions.cancel'),
                        action: () => (showDeleteModal = false),
                    },
                ]"
                @closed="showDeleteModal = false"
            >
                <span class="typographies-paragraph-medium-regular">
                    {{ $t.__('common.labels.delete_address_description') }}
                </span>
            </CompassModal>
        </template>
    </tab-layout>
</template>

<script>
import OverviewTabMixin from './OverviewTabMixin';
import AddressMap from '../../AddressMap.vue';
import AddressCard from 'vcomponents/common/address/AddressCard.vue';
import Preloader from 'vcomponents/utilities/Preloader.vue';
import TabLayout from 'vcomponents/overview/tabs/TabLayout.vue';

import { createNamespacedHelpers } from 'vuex';
import { deleteAddress, fetchAddresses, updateAddress } from '../../../api/addressesService';
import { getOverviewLinkedType } from '../../../helpers/overviewHelpers';
const popupPanelMapActions = createNamespacedHelpers('popupPanel').mapActions;
const corpState = createNamespacedHelpers('corp').mapState;

export default {
    components: {
        Preloader,
        AddressMap,
        TabLayout,
        AddressCard,
    },
    mixins: [OverviewTabMixin],
    data() {
        return {
            addresses: [],
            corpAddresses: [],
            fullMapVisible: false,
            disableMap: true,
            loading: true,
            buttons: [], // workaround for pv5 feature detection issue SAAS-22962
            showDeleteModal: false,
            primaryActionDeleteAddress: {
                label: this.$t.__('common.actions.delete'),
                type: 'primary-destructive',
                action: () => null,
                loading: false,
            },
            overviewRelated: null,
            addressUpdating: null,
            addressSelected: null,
        };
    },

    computed: {
        ...corpState({ mainCorpAddress: 'mainAddress' }),
        mainInvoicingAddress() {
            return this.addresses.find((item) => item.is_invoicing_address);
        },
        mainDeliveryAddress() {
            return this.addresses.find((item) => item.is_delivery_address);
        },
        otherAddresses() {
            return this.addresses.filter(
                (item) => !item.is_invoicing_address && !item.is_delivery_address,
            );
        },
        permissions() {
            return this.$store.getters.permissions;
        },
        titleMainAddress() {
            if (this.mainInvoicingAddress.is_delivery_address) {
                return this.$t.__(
                    'overview.tabs.TabAddresses.title_invoicing_and_delivery_address',
                );
            } else {
                return this.$t.__('overview.tabs.TabAddresses.title_invoicing_address');
            }
        },
    },

    created() {
        this.overviewRelated = getOverviewLinkedType(this.linkedtype);
        if (this.more.buttons && this.more.buttons.length > 0) {
            this.more.buttons.map((button) => {
                button.linkedid = this.linkedid;
                button.linkedtype = this.linkedtype;
            });
        }
        this.buttons = [...this.buttons, this.more.buttons.find((button) => button.id === 'add')]; // add address is always present
        this.displaySeeMapButton();
        EventBus.on('add-address', (item) => {
            this.addresses.push(item);
            this.displaySeeMapButton();
        });

        EventBus.on('edit-address', (address) => {
            this.addresses = this.addresses.map((item) => {
                if (item.id !== address.id) {
                    return item;
                }
                return { ...item, ...address };
            });
        });

        this.fetch();
    },
    beforeUnmount() {
        this.close({ key: 'addressForm' });
    },
    methods: {
        ...popupPanelMapActions(['open', 'close', 'feedProps']),
        async fetch() {
            try {
                this.loading = true;
                this.addresses = await fetchAddresses(
                    this.overviewRelated?.apiEndpoint,
                    this.linkedid,
                );
                this.checkMapBtn();
            } finally {
                this.loading = false;
            }
        },
        deleteAddress(address) {
            this.showDeleteModal = true;
            this.primaryActionDeleteAddress.action = () => {
                this.primaryActionDeleteAddress.loading = true;
                deleteAddress(this.overviewRelated.apiEndpoint, this.linkedid, address.id)
                    .then(() => {
                        this.addresses = this.addresses.filter((item) => item.id !== address.id);
                        this.showDeleteModal = false;
                        this.$pushToast({
                            type: 'success',
                            text: this.$t.__('common.labels.delete_address_success'),
                            icon: true,
                        });
                        this.displaySeeMapButton();
                    })
                    .catch((e) => {
                        this.$pushToast({
                            type: 'error',
                            text: this.$t.__('common.labels.delete_address_error'),
                            icon: true,
                        });
                        throw e;
                    })
                    .finally(() => (this.primaryActionDeleteAddress.loading = false));
            };
        },

        checkMapBtn() {
            this.disableMap = !this.addresses.some(
                (address) => address.geocode.lng !== null && address.geocode.lat !== null,
            );

            this.displaySeeMapButton();
        },
        openFullMap() {
            if (this.addresses.some((address) => address.lng !== null && address.lat !== null)) {
                this.fullMapVisible = true;
            } else {
                this.$pushToast({
                    type: 'error',
                    text: this.$t.__('overview.tabs.TabAddresses.toast_error_geolocation'),
                    icon: true,
                });
            }
        },
        closeFullMap() {
            this.fullMapVisible = false;
        },

        async defineInvoicingAddress(address) {
            try {
                this.addressUpdating = address.id;
                const currentInvoicingAddress = this.addresses.find(
                    (item) => item.is_invoicing_address,
                );

                await updateAddress(this.overviewRelated?.apiEndpoint, this.linkedid, address.id, {
                    ...address,
                    is_invoicing_address: true,
                });

                this.addresses = this.addresses.map((item) => {
                    if (item.id === currentInvoicingAddress.id) {
                        return {
                            ...item,
                            is_invoicing_address: false,
                        };
                    } else if (item.id === address.id) {
                        return {
                            ...item,
                            is_invoicing_address: true,
                        };
                    }
                    return item;
                });
                if (this.addressSelected?.id === currentInvoicingAddress.id) {
                    this.feedProps({
                        key: 'addressForm',
                        props: {
                            address: {
                                ...this.addressSelected,
                                is_invoicing_address: false,
                            },
                        },
                    });
                }
            } finally {
                this.addressUpdating = null;
            }
        },
        async defineDeliveryAddress(address) {
            try {
                this.addressUpdating = address.id;
                const currentDeliveryAddress = this.addresses.find(
                    (item) => item.is_delivery_address,
                );

                await updateAddress(this.overviewRelated?.apiEndpoint, this.linkedid, address.id, {
                    is_delivery_address: true,
                });

                this.addresses = this.addresses.map((item) => {
                    if (item.id === currentDeliveryAddress?.id) {
                        return {
                            ...item,
                            is_delivery_address: false,
                        };
                    } else if (item.id === address.id) {
                        return {
                            ...item,
                            is_delivery_address: true,
                        };
                    }
                    return item;
                });
                if (this.addressSelected?.id === currentDeliveryAddress?.id) {
                    this.feedProps({
                        key: 'addressForm',
                        props: {
                            address: {
                                ...this.addressSelected,
                                is_delivery_address: false,
                            },
                        },
                    });
                }
            } finally {
                this.addressUpdating = null;
            }
        },
        openAddress(address) {
            this.addressSelected = address ?? null;

            this.open({
                key: 'addressForm',
                props: {
                    linkedid: this.linkedid,
                    linkedtype: this.linkedtype,
                    address: address,
                    apiEndpoint: this.overviewRelated?.apiEndpoint,
                    callback: () => {
                        this.addressSelected = null;
                    },
                },
            });
        },
        displaySeeMapButton() {
            if (this.addresses.length > 0) {
                const checkButtonAlreadyExist = this.more.buttons.find(
                    (button) => button.id === 'seemap',
                );
                if (!checkButtonAlreadyExist) {
                    this.more.buttons.push({
                        id: 'seemap',
                        type: 'function',
                        label: this.$l10n.__('Voir la carte'),
                        dataUpsell: 'map_listing',
                        primary: false,
                        event: 'openAddressMap',
                    });
                }
            } else {
                this.more.buttons = this.more.buttons.filter((button) => button.id !== 'seemap');
            }
            this.buttons = this.more.buttons; // update buttons with updated props
            this.$nextTick(() => {
                window.createUpsellButtons(); // update DOM after address added
            });
        },
    },
};
</script>

<style lang="scss">
.tab-addresses__list-addresses {
    display: flex;
    flex-direction: column;
    gap: var(--spacing-5);

    h4 {
        margin-top: 0;
    }
}

.address-card-container {
    display: flex;
    flex-direction: column;
    gap: var(--spacing-4);
}
</style>
